import type { AccessibilityProps } from 'react-native';

import type { SpacingSystemProps, SxStyleProp } from '@rover/kibble/core/styledSystemPropTypes';
import type {
  FontWeightProps,
  TextAlignProps,
  TextColorProps,
} from '@rover/kibble/styles/styled-system';
import type { ThemeHeadingSizes } from '@rover/kibble/styles/themeTypes';

/** The elements Heading and Display can render on web. */
export type HeadingTags = 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/** Web-only props. Ignored on React Native. */
export type HeadingWebOnlyProps = {
  /** Hides the heading visually but keeps it for screen readers. Defaults to `false`. */
  a11yHidden?: boolean;
  /**
   * The element to render, such as `h1`. Defaults to the element for `size`. Required when `size`
   * is a responsive array.
   */
  as?: HeadingTags;
  /** A CSS class name. Avoid it: use props, then `sx`. */
  className?: string;
  /** Grows sizes `400` and up on screens 768px and wider. Defaults to `true`. */
  responsive?: boolean;
  /** The CSS `display` value. */
  display?: string;
};

/** React Native-only props. Ignored on web. */
export type HeadingRxnOnlyProps = AccessibilityProps;

/** Props that apply on web and React Native. */
export type BaseHeadingProps = SpacingSystemProps &
  TextAlignProps &
  TextColorProps &
  FontWeightProps & {
    /** The heading's text. */
    children: React.ReactNode;
    /**
     * The type style. On web it also picks the element, such as `h1` for `500` and a `div` for
     * `600`. A responsive array needs `webProps.as`, or Heading throws. Defaults to `200`.
     */
    size?: ThemeHeadingSizes | ThemeHeadingSizes[];
    /** The heading's `id`, such as the target of an in-page link. */
    id?: string;
    /** Style overrides that read theme tokens. Use props first. */
    sx?:
      | SxStyleProp
      | Record<string, string | number | undefined | Array<string | number | undefined>>;
    /** Finds the heading in tests. Rendered as `data-testid` on web. */
    testID?: string;
  };

export type HeadingCommonProps = BaseHeadingProps & {
  /**
   * Web-only props, such as `as` and `a11yHidden`, and web overrides of other props. Ignored on
   * React Native.
   */
  webProps?: Partial<Omit<BaseHeadingProps, keyof HeadingWebOnlyProps>> & HeadingWebOnlyProps;
  /** React Native props and overrides, ignored on web. `accessibilityRole` defaults to `header`. */
  rxnProps?: Partial<Omit<BaseHeadingProps, keyof HeadingRxnOnlyProps>> & HeadingRxnOnlyProps;
};

const MARGIN_BOTTOM_PROPS = ['m', 'margin', 'my', 'marginY', 'mb', 'marginBottom'] as const;

type MarginBottomProps = Pick<SpacingSystemProps, (typeof MARGIN_BOTTOM_PROPS)[number]>;

/**
 * A responsive `size` array makes styled-system's `variant()` expand the size
 * variant's built-in `marginBottom` into `@media` blocks, which override a
 * literal `mb` prop (that only emits a base rule). When the caller passes any
 * margin-bottom-affecting spacing prop, drop `marginBottom` from every size
 * variant so the `space` composer becomes the sole source of `margin-bottom`
 * and wins at every breakpoint. Mirrors the `fontWeight` strip in Text.tsx.
 *
 * Web-only: the React Native variants don't have this media-query cascade.
 */
export function getVariantStylesForProps<T extends Record<string, object>>(
  variants: T,
  props: MarginBottomProps
): T {
  const overridesMarginBottom = MARGIN_BOTTOM_PROPS.some((prop) => props[prop] !== undefined);
  if (!overridesMarginBottom) {
    return variants;
  }
  return Object.fromEntries(
    Object.entries(variants as Record<string, { marginBottom?: unknown }>).map(
      // marginBottom is destructured out to remove it from the returned object
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([key, { marginBottom, ...value }]) => [key, value]
    )
  ) as T;
}
